import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSignalRContext } from '../contexts/SignalRContext.tsx';
import { useGame } from '../contexts/GameContext.tsx';
import GameBoard from '../components/Game/GameBoard.tsx';
import Modal from '../components/common/Modal.tsx';

type SosCombo = {
  coords: number[][];
  direction: 'horizontal' | 'vertical' | 'diagonal-right' | 'diagonal-left';
};

function getDirection(coords: number[][]): 'horizontal' | 'vertical' | 'diagonal-right' | 'diagonal-left' {
  const [[r1, c1], [r2, c2], [r3, c3]] = coords;
  if (r1 === r2 && r2 === r3) return 'horizontal';
  if (c1 === c2 && c2 === c3) return 'vertical';
  if ((r3 - r1) === (c3 - c1)) return 'diagonal-right';
  if ((r3 - r1) === -(c3 - c1)) return 'diagonal-left';
  return 'horizontal';
}

// Kazananı skor tablosundan bulmak için fonksiyon
function getWinnerFromScores(scores: Record<string, number>) {
  const entries = Object.entries(scores);
  if (entries.length === 0) return null;
  const maxScore = Math.max(...entries.map(([_, score]) => score));
  const winners = entries.filter(([_, score]) => score === maxScore);
  if (winners.length === 1) return winners[0][0];
  return null; // Berabere
}


const GamePage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { connection, isConnected } = useSignalRContext();
  const { state, dispatch } = useGame();
  const [redirecting, setRedirecting] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState<'S' | 'O'>('S');
  const [sosCoords, setSosCoords] = useState<number[][][]>([]);
  const sosTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [allSosCombos, setAllSosCombos] = useState<SosCombo[]>(() => {
    const saved = localStorage.getItem(`sosCombos-${roomId}`);
    return saved ? JSON.parse(saved) : [];
  });
  const [showEndModal, setShowEndModal] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showHomeModal, setShowHomeModal] = useState(false);

  const username = location.state?.username || localStorage.getItem('sos-username') || '';

  useEffect(() => {
    if (!location.state || !username) {
      setRedirecting(true);
      setTimeout(() => navigate('/'), 2000);
    }
  }, [location.state, username, navigate]);

  useEffect(() => {
    if (!connection || !roomId) {
      // console.log('SignalR connection or roomId missing', connection, roomId);
      return;
    }
    // console.log('Setting up gamestateupdated handler');
    const handleGameStateUpdated = (gameState: any) => {
      // console.log('gamestateupdated:', gameState);
      dispatch({ type: 'SET_BOARD', payload: gameState.board });
      if (gameState.currentPlayer) {
        dispatch({
          type: 'SET_CURRENT_PLAYER',
          payload: {
            id: gameState.currentPlayer,
            name: gameState.currentPlayer,
            symbol: gameState.currentPlayer === username ? selectedSymbol : (selectedSymbol === 'S' ? 'O' : 'S')
          }
        });
      }
      if (gameState.scores) {
        dispatch({ type: 'SET_SCORES', payload: gameState.scores });
      }
      dispatch({ type: 'SET_GAME_STATUS', payload: gameState.winner ? 'finished' : 'playing' });
      
      // SOS koordinatlarını güncelle ve animasyon için timeout ayarla
      if (gameState.sosCoordinates && Array.isArray(gameState.sosCoordinates)) {
        // console.log('SOS Coordinates received:', gameState.sosCoordinates);
        setSosCoords(gameState.sosCoordinates);
        
        // Önceki timeout'u temizle
        if (sosTimeoutRef.current) {
          clearTimeout(sosTimeoutRef.current);
        }
        
      }

      // Eğer board tamamen sıfırsa (yeni oyun başladıysa), SOS çizgilerini ve localStorage'ı temizle
      const isBoardReset = Array.isArray(gameState.board) && gameState.board.every((row: string[]) => row.every(cell => cell === '' || cell === 'S'));
      if (isBoardReset) {
        setAllSosCombos([]);
        localStorage.removeItem(`sosCombos-${roomId}`);
        // Skorları backend'den gelecek, burada sıfırlamaya gerek yok
      }
    };

    const handlePlayerListUpdated = (players: string[]) => {
      dispatch({ type: 'SET_PLAYERS', payload: players });
    };

    const handlePlayerJoined = (name: string) => {
      // console.log('playerjoined:', name);
    };

    connection.on('gamestateupdated', handleGameStateUpdated);
    connection.on('playerlistupdated', handlePlayerListUpdated);
    connection.on('playerjoined', handlePlayerJoined);
    connection.on('gamestarted', () => {
      // oyun başladı
    });

    // Reconnect olursa handler'ı tekrar ekle
    connection.onreconnected(() => {
      connection.on('gamestateupdated', handleGameStateUpdated);
    });

    const timeoutRef = sosTimeoutRef.current;
    return () => {
      connection.off('gamestateupdated', handleGameStateUpdated);
      connection.off('playerlistupdated', handlePlayerListUpdated);
      connection.off('playerjoined', handlePlayerJoined);
      connection.off('gamestarted');
      // Cleanup timeout
      if (timeoutRef) {
        clearTimeout(timeoutRef);
      }
    };
  }, [connection, roomId, dispatch, username, selectedSymbol, state.players]);

  useEffect(() => {
    if (!connection || !roomId) return;
    const fetchPlayerList = async () => {
      try {
        const players = await connection.invoke('GetPlayerList', roomId);
        dispatch({ type: 'SET_PLAYERS', payload: players });
      } catch {
        // ignore
      }
    };
    fetchPlayerList();
  }, [connection, roomId, dispatch]);

  useEffect(() => {
    if (!connection || !roomId) return;
    const fetchGameState = async () => {
      try {
        const gameState = await connection.invoke('GetGameState', roomId);
        // console.log('GetGameState result:', gameState);
        if (gameState) {
          dispatch({ type: 'SET_BOARD', payload: gameState.board });
          if (gameState.currentPlayer) {
            dispatch({
              type: 'SET_CURRENT_PLAYER',
              payload: {
                id: gameState.currentPlayer,
                name: gameState.currentPlayer,
                symbol: gameState.currentPlayer === username ? selectedSymbol : (selectedSymbol === 'S' ? 'O' : 'S')
              }
            });
          }
          if (gameState.scores) {
            dispatch({ type: 'SET_SCORES', payload: gameState.scores });
          }
          dispatch({ type: 'SET_GAME_STATUS', payload: gameState.winner ? 'finished' : 'playing' });
        }
      } catch (err) {
        // console.error('GetGameState error:', err);
      }
    };
    fetchGameState();
  }, [connection, roomId, dispatch, username, selectedSymbol]);

  useEffect(() => {
    if (!connection || !roomId || !username) return;
    // Bağlantı kurulduğunda kullanıcıyı tekrar odaya ekle
    const joinRoom = async () => {
      try {
        await connection.invoke('JoinRoom', roomId, username);
      } catch (err) {
        // console.error('JoinRoom error:', err);
      }
    };
    joinRoom();
  }, [connection, roomId, username]);

  useEffect(() => {
    if (state.gameStatus === 'finished') {
      setShowEndModal(true);
    } else {
      setShowEndModal(false);
    }
  }, [state.gameStatus]);


  useEffect(() => {
    if (sosCoords && Array.isArray(sosCoords)) {
      setAllSosCombos(prev => {
        const merged = [...prev];
        sosCoords.forEach(newCoords => {
          const direction = getDirection(newCoords);
          const exists = merged.some(combo =>
            JSON.stringify(combo.coords) === JSON.stringify(newCoords)
          );
          if (!exists) {
            merged.push({ coords: newCoords, direction });
          }
        });
        localStorage.setItem(`sosCombos-${roomId}`, JSON.stringify(merged));
        return merged;
      });
    }
  }, [sosCoords, roomId, dispatch, selectedSymbol, username]);

  useEffect(() => {
    if (!connection) return;
    const handleGameStarted = () => {
      // setShowEndModal(false); // This state variable is not defined in the original file
    };
    connection.on('gamestarted', handleGameStarted);
    return () => {
      connection.off('gamestarted', handleGameStarted);
    };
  }, [connection]);


  useEffect(() => {
    if (!connection) return;
    const handleForceRestart = () => {
      setShowEndModal(false);
      setAllSosCombos([]);
      localStorage.removeItem(`sosCombos-${roomId}`);
    };
    connection.on('forceRestartGame', handleForceRestart);
    return () => {
      connection.off('forceRestartGame', handleForceRestart);
    };
  }, [connection, roomId]);




  const handleCellClick = async (row: number, col: number) => {
    if (!connection || !roomId || state.gameStatus !== 'playing') return;
    try {
      await connection.invoke('MakeMove', roomId, row, col, selectedSymbol);
    } catch (error) {
      // console.error('Error making move:', error);
    }
  };

  const handleResetGame = async () => {
    if (!connection || !roomId) return;
    setResetting(true);
    try {
      await connection.invoke('StartNewGame', roomId);
      setShowEndModal(false);
      setResetting(false);
      setAllSosCombos([]); // SOS çizgilerini sıfırla
      localStorage.removeItem(`sosCombos-${roomId}`); // localStorage'dan da sil
    } catch (err) {
      setResetting(false);
      // Hata yönetimi eklenebilir
    }
  };

  const handleGoHome = () => {
    // Oyunu bitir ve ana sayfaya git
    if (connection && roomId) {
      try {
        // Odayı terk et (opsiyonel - backend'de LeaveRoom varsa)
        connection.invoke('LeaveRoom', roomId).catch(() => {
          // Hata olursa yok say
        });
      } catch (err) {
        // Hata yönetimi
      }
    }
    // Game state'i sıfırla
    dispatch({ type: 'RESET_GAME' });
    // Ana sayfaya git
    navigate('/');
  };

  if (redirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800">Redirecting...</h2>
          <p className="text-gray-600">You must join a room first. Returning to home.</p>
        </div>
      </div>
    );
  }

  if (!isConnected) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800">Connecting...</h2>
          <p className="text-gray-600">Please wait while we connect to the game server.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Oyun Sonu Modalı */}
      <Modal isOpen={showEndModal} onClose={() => setShowEndModal(false)} title="Oyun Bitti!">
        <div className="flex flex-col items-center gap-4">
          <div className="text-lg font-bold text-green-600">
            Kazanan: {getWinnerFromScores(state.scores) ? getWinnerFromScores(state.scores) : 'Berabere'}
          </div>
          <button
            className="px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-60"
            onClick={handleResetGame}
            disabled={resetting}
          >
            {resetting ? 'Yeniden Başlatılıyor...' : 'Yeni Oyuna Başla'}
          </button>
        </div>
      </Modal>

      {/* Go to Home Modal */}
      <Modal isOpen={showHomeModal} onClose={() => setShowHomeModal(false)} title="Return to Home">
        <div className="flex flex-col items-center gap-4">
          <p className="text-gray-700 text-center">
            Are you sure you want to return to the home page? The game will end.
          </p>
          <div className="flex gap-3">
            <button
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition"
              onClick={() => setShowHomeModal(false)}
            >
              No
            </button>
            <button
              className="px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
              onClick={handleGoHome}
            >
              Yes
            </button>
          </div>
        </div>
      </Modal>
      {/* Ana içerik */}
      <div className="min-h-screen bg-gradient-to-br from-blue-100 to-purple-200 flex flex-col items-center py-8 px-2 sm:px-4">
        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-8 w-full max-w-2xl mb-4">
          <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2 sm:gap-0">
            <div className="w-full sm:w-auto">
              <div className="flex items-center gap-2 sm:gap-3 mb-2">
                <img 
                  src="/logo192.png" 
                  alt="SOS Game Logo" 
                  className="w-8 h-8 sm:w-10 sm:h-10 object-contain cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => setShowHomeModal(true)}
                />
                <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-800">SOS Game</h1>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-gray-500">Room:</span>
                <span className="font-mono font-bold text-base sm:text-lg bg-gray-100 px-2 py-1 rounded select-all">{roomId}</span>
              </div>
            </div>
            <div className="w-full sm:w-auto flex justify-between items-center">
              <span className="text-gray-500">Current Player:</span>
              <span className="font-bold text-blue-700 ml-1">{state.currentPlayer?.name}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:gap-4 mb-4">
            {state.players.map((player, idx) => {
              const isCurrentPlayer = state.currentPlayer?.name === player;
              const isYou = player === username;
              return (
                <div key={idx} className={`px-2 py-2 sm:px-4 sm:py-2 rounded-lg shadow relative text-xs sm:text-base transition-all duration-300 flex items-center justify-center ${
                  isCurrentPlayer 
                    ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-400 shadow-lg shadow-green-200' 
                    : isYou 
                      ? 'bg-blue-100 text-blue-700 font-bold' 
                      : 'bg-gray-100 text-gray-700'
                }`}>
                  {isCurrentPlayer && (
                    <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-[10px] sm:text-xs font-bold px-2 py-0.5 rounded-full shadow-md whitespace-nowrap">
                       Active Player 
                    </div>
                  )}
                  <div className={`flex items-center justify-center gap-1 sm:gap-2 ${isCurrentPlayer ? 'mt-2' : ''}`}>
                    <span>{player}</span>
                    {isYou && <span className="text-xs text-green-500">(You)</span>}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col items-center w-full">
            <div className="flex flex-col items-center gap-3 w-full">
              <div className="flex flex-col items-center w-full">
                <span className="text-gray-600 font-semibold block mb-2 text-center w-full">Scores</span>
                <div className="flex flex-wrap gap-2 justify-center items-center w-full">
                  {state.players.map((player) => {
                    const isCurrentPlayer = state.currentPlayer?.name === player;
                    const isYou = player === username;
                    const score = state.scores[player] || 0;
                    return (
                      <div
                        key={player}
                        className={`flex items-center justify-center gap-2 px-3 py-1.5 rounded-xl border text-sm sm:text-base shadow-sm transition-all ${
                          isCurrentPlayer
                            ? 'border-green-300 bg-green-50 text-green-700 shadow-green-100'
                            : isYou
                              ? 'border-blue-300 bg-blue-50 text-blue-700'
                              : 'border-gray-200 bg-white text-gray-700'
                        }`}
                      >
                        <span className={`font-semibold ${isYou ? 'text-blue-600' : ''}`}>{player}</span>
                        <span className="text-gray-400">•</span>
                        <span className="font-bold text-gray-800">{score}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              {state.winner && (
                <div className="text-base sm:text-xl font-bold text-green-600 text-center">Winner: {state.winner}</div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-2 sm:p-6 flex flex-col sm:flex-row items-stretch gap-4 sm:gap-8 w-full max-w-2xl mx-auto overflow-x-auto">
          <div className="w-full overflow-x-auto">
            <GameBoard 
              onCellClick={handleCellClick} 
              roomId={roomId} 
              sosCombos={allSosCombos}
            />
          </div>
          <div className="flex-1 flex flex-col justify-center items-center gap-2 h-full mt-4 sm:mt-0">

            <span className="text-gray-600 font-semibold mb-2">Harf Seç:</span>
            <div className="flex gap-2">
              <button
                className={`px-4 py-2 rounded ${selectedSymbol === 'S' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                onClick={() => setSelectedSymbol('S')}
              >
                S
              </button>
              <button
                className={`px-4 py-2 rounded ${selectedSymbol === 'O' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                onClick={() => setSelectedSymbol('O')}
              >
                O
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 w-full max-w-2xl flex flex-col items-center gap-2 text-xs text-gray-400">
          <div>Made with <span className="text-pink-500">♥</span> for your special game night!</div>
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
            <a
              href="mailto:info@playsos.online"
              className="underline hover:text-blue-600 transition flex items-center gap-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline-block text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              info@playsos.online
            </a>
            <a
              href="https://buymeacoffee.com/metinhan"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-amber-600 transition flex items-center gap-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline-block text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Support me
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default GamePage;
